/**
 * Animate on scroll. `data-scroll-reveal="{name}"` toggles `ani-{Name}`,
 * `ani-{Name}-hidden`, `ani-{Name}-active` (empty value = `Reveal`).
 *
 * - `data-scroll-reveal-repeat` - replay every time it re-enters view.
 * - `data-scroll-reveal-breakpoints` - restrict to `mobile`/`tablet`/`desktop`, space-separated. Checked once on load.
 */

window.BAO.ScrollReveal = new (class ScrollReveal {
  get attribute () {
    return 'data-scroll-reveal'
  }

  get repeatAttribute () {
    return 'data-scroll-reveal-repeat'
  }

  get breakpointsAttribute () {
    return 'data-scroll-reveal-breakpoints'
  }

  get defaultAnimation () {
    return 'Reveal'
  }

  get rootMargin () {
    return '0px 0px -50px 0px'
  }

  constructor () {
    this.handleIntersection = this.handleIntersection.bind(this)
    this.observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: this.rootMargin,
    })
  }

  /**
   * @param {ParentNode} [root]
   *
   * @returns {void}
   */
  initialize (root = document) {
    const elements = Array.from(root.querySelectorAll(`[${this.attribute}]`)).filter(
      (element) => this.isEnabledForViewport(element),
    )

    if (elements.length === 0) return
    if (Shopify.designMode) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    elements.forEach((element) => {
      element.classList.add(this.getAnimationClassName(element))
      this.setRevealed(element, false)
      this.observer.observe(element)
    })
  }

  /**
   * @param {HTMLElement} element
   * @param {'hidden'|'active'} [state]
   *
   * @returns {string}
   */
  getAnimationClassName (element, state) {
    const value = element.getAttribute(this.attribute)
    const name = value ? value.charAt(0).toUpperCase() + value.slice(1) : this.defaultAnimation

    return state ? `ani-${name}-${state}` : `ani-${name}`
  }

  /**
   * @param {'mobile'|'tablet'|'desktop'} breakpoint
   *
   * @returns {boolean}
   */
  matchesBreakpoint (breakpoint) {
    const { sm, lg } = window.BAO.MediaBreakpoints.breakpoints

    if (breakpoint === 'mobile') return window.innerWidth < sm
    if (breakpoint === 'tablet') return window.innerWidth >= sm && window.innerWidth < lg
    if (breakpoint === 'desktop') return window.innerWidth >= lg

    return false
  }

  /**
   * @param {HTMLElement} element
   *
   * @returns {boolean}
   */
  isEnabledForViewport (element) {
    const breakpoints = element.getAttribute(this.breakpointsAttribute)

    return !breakpoints || breakpoints.split(' ').some((breakpoint) => this.matchesBreakpoint(breakpoint))
  }

  /**
   * @param {HTMLElement} element
   * @param {boolean} revealed
   *
   * @returns {void}
   */
  setRevealed (element, revealed) {
    element.classList.toggle(this.getAnimationClassName(element, 'active'), revealed)
    element.classList.toggle(this.getAnimationClassName(element, 'hidden'), !revealed)
  }

  /**
   * @param {HTMLElement} element
   *
   * @returns {void}
   */
  revealElement (element) {
    this.setRevealed(element, true)

    const activeClassName = this.getAnimationClassName(element, 'active')

    const onAnimationEnd = (event) => {
      if (event.target !== element && event.target.parentElement !== element) return

      element.classList.remove(activeClassName)
      element.removeEventListener('transitionend', onAnimationEnd)
      element.removeEventListener('animationend', onAnimationEnd)
    }

    element.addEventListener('transitionend', onAnimationEnd)
    element.addEventListener('animationend', onAnimationEnd)
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   *
   * @returns {void}
   */
  handleIntersection (entries) {
    entries.forEach((entry) => {
      const element = entry.target
      const repeats = element.hasAttribute(this.repeatAttribute)

      if (entry.isIntersecting) {
        this.revealElement(element)

        if (!repeats) this.observer.unobserve(element)
      } else if (repeats) {
        this.setRevealed(element, false)
      }
    })
  }
})()

window.addEventListener('DOMContentLoaded', () => window.BAO.ScrollReveal.initialize())

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => window.BAO.ScrollReveal.initialize(event.target))
}
